import content from 'content.json';
import React from 'react';
import { Image } from 'react-bootstrap';
const { inbox } = content;
import { UserStyles } from 'types/styles/user';
import { Client } from 'types/user';

// extract values from array
// if it is an array of strings return as is
// if it's array of key value pairs, return the values
const extractValues = (arr?: any[]): string[] => {
    if (!Array.isArray(arr)) return [];
    return arr
        ?.map((item) =>
            typeof item === 'string' ? item : item.value || item.picture || item.name || ''
        )
        .filter(Boolean);
};

const prefixes: Map<string, string | React.ReactNode> = new Map([
    ['goals_new', 'Need'],
    ['events', 'Events'],
    ['work_type', 'Dress code'],
    ['holiday_type', 'Holiday'],
    ['clothing_categories', 'Looking to buy'],
    ['brands', 'Brands'],
    ['highlight', 'Highlight'],
    ['fit_top', 'Fit'],
    ['fit_bottom', 'Fit'],
    ['fabrics', "Don't use"],
    ['what_matters', 'Cares about'],
    ['heels', 'Heels'],
    ['referral', 'Referral'],
    ['occupation', 'Occupation'],
    ['colors_and_prints_objects', 'Preferred colors'],
    [
        'colors_to_avoid',
        <>
            <b>Avoid</b> colors
        </>
    ],
    [
        'patterns_to_avoid',
        <>
            <b>Avoid</b> patterns
        </>
    ],
    [
        'necklines_to_avoid',
        <>
            <b>Avoid</b> necklines
        </>
    ]
] as Array<[string, string | React.ReactNode]>);

export type InfoItemFormatter = (args: {
    style: UserStyles;
    client: Client;
}) => string | React.ReactNode;

export interface InfoItemConfig {
    icon: string;
    formatter: InfoItemFormatter;
}

const noLabel = (value?: React.ReactNode | string | null): React.ReactNode => withLabel('', value);

const withLabel = (name: string, value?: React.ReactNode | string | null): React.ReactNode => {
    if (!value) return null;
    const prefix = prefixes.get(name);
    if (!prefix) {
        return value;
    }
    return (
        <>
            <span>{prefix}:</span>&nbsp;<b>{value}</b>
        </>
    );
};

const renderWithSeparator = (nodes: React.ReactNode[], separator = ', '): React.ReactNode => {
    const filteredNodes = nodes.filter(Boolean);
    if (filteredNodes.length === 0) {
        return null;
    }
    return (
        <>
            {filteredNodes.map((node, idx, arr) => (
                <span key={idx}>
                    {node}
                    {idx < arr.length - 1 && separator}
                </span>
            ))}
        </>
    );
};

export const styleItemConfig: Record<string, InfoItemConfig> = {
    location: {
        icon: inbox.icons.location,
        formatter: ({ style, client }) => noLabel(style?.locations || client?.location)
    },
    gender: {
        icon: inbox.icons.gender,
        formatter: ({ style, client }) => {
            const value = [
                style?.gender,
                style?.age || client.age_partners || client.age,
                style?.zodiac_sign
            ]
                .filter(Boolean)
                .join(', ');
            return noLabel(value);
        }
    },
    body_tags: {
        icon: inbox.icons.body_tags,
        formatter: ({ style }) => {
            const tags = (style?.body_tags || []).map((tag) => tag.name);
            tags.push(style?.height?.value);
            const value = tags.filter(Boolean).join(', ');
            return withLabel('body_tags', value);
        }
    },
    occupation: {
        icon: inbox.icons.occupation,
        formatter: ({ style }) => withLabel('occupation', style?.occupation)
    },
    referral: {
        icon: inbox.icons.referral,
        formatter: ({ style }) => withLabel('referral', style?.referral)
    },
    style_icons: {
        icon: inbox.icons.style_icons,
        formatter: ({ style }) => {
            const values = [...style?.style_icons, style.style_icons_comments];
            const value = values.filter(Boolean).join(', ');
            return noLabel(value);
        }
    },
    goals_new: {
        icon: inbox.icons.goals_new,
        formatter: ({ style }) => {
            const tags = style?.goals_new || [];
            const goals = tags
                .map((tag) => tag.value)
                .filter(Boolean)
                .join(', ');
            const value = [goals, style?.goal_other_text].filter(Boolean).join(' - ');
            return withLabel('goals_new', value);
        }
    },
    events: {
        icon: '',
        formatter: ({ style }) => {
            const events = (style?.events || []).filter(Boolean).join(', ');
            const value = [events, style?.event_other_text].filter(Boolean).join(' - ');
            return withLabel('events', value);
        }
    },
    work_type: {
        icon: inbox.icons.work_type,
        formatter: ({ style }) => {
            const value = [style?.work_type?.value, style?.work_comments]
                .filter(Boolean)
                .join(', ');
            return withLabel('work_type', value);
        }
    },
    holiday_type: {
        icon: inbox.icons.holiday_type,
        formatter: ({ style }) => {
            const tags = style?.holiday_type || [];
            const value = [...tags, style.holiday_other_text].filter(Boolean).join(', ');
            return withLabel('holiday_type', value);
        }
    },
    dress_size: {
        icon: inbox.icons.dress_size,
        formatter: ({ style }) => {
            const props = [
                'dress_size',
                'bottom_size',
                'shoes_size',
                'top_size',
                'jeans_size',
                'size_outerwear',
                'size_one_piece',
                'size_underwear'
            ] as const;
            const nodes = props.map((prop) => {
                const val = style?.[prop];
                if (val) {
                    return (
                        <span key={prop}>
                            {inbox.bookings.info.sizes[prop]}: <b>{String(val)}</b>
                        </span>
                    );
                }
                return null;
            });
            return withLabel('dress_size', renderWithSeparator(nodes));
        }
    },
    clothing_categories: {
        icon: inbox.icons.clothing_categories,
        formatter: ({ style }) => {
            const categories = extractValues(style?.clothing_categories);
            return withLabel('clothing_categories', categories.join(', '));
        }
    },
    brands: {
        icon: inbox.icons.brands,
        formatter: ({ style }) => {
            const brands = extractValues(style?.brands);
            return withLabel('brands', brands.join(', '));
        }
    },
    highlight: {
        icon: inbox.icons.highlight,
        formatter: ({ style }) => {
            const highlight = extractValues(style.highlight);
            return withLabel('highlight', highlight.join(', '));
        }
    },
    colors_and_prints_objects: {
        icon: inbox.icons.colors_and_prints_objects,
        formatter: ({ style }) => {
            const isAnyColor =
                style?.colors_and_prints_objects?.filter(({ key }) => key === 'COLOR_ALL').length >
                0;
            if (isAnyColor) {
                return noLabel('Anything goes');
            }
            const uniqueByKey = Array.from(
                new Map(style?.colors_and_prints_objects?.map(obj => [obj.key, obj])).values()
            );

            const colors = uniqueByKey?.map(({ key, picture, name }) => (
                <Image key={key} src={picture} title={name} />
            ));
            return withLabel('colors_and_prints_objects', renderWithSeparator(colors, ' '));
        }
    },
    colors_to_avoid: {
        icon: inbox.icons.colors_to_avoid,
        formatter: ({ style }) => {
            const colors = (style?.colors_to_avoid ?? []).map(({ key, picture, name }) => (
                <Image key={key} src={picture} title={name} />
            ));
            const tmp = colors.length > 0 ? renderWithSeparator(colors, ' ') : 'No colors selected';
            const comment = style?.colors_to_avoid_comment ? (
                <div>Comment: {style.colors_to_avoid_comment}</div>
            ) : null;
            const value = (
                <>
                    {tmp}
                    {comment}
                </>
            );
            return withLabel('colors_to_avoid', value);
        }
    },
    patterns_to_avoid: {
        icon: inbox.icons.patterns_to_avoid,
        formatter: ({ style }) => {
            const patterns = (style?.patterns_to_avoid ?? []).map(({ key, picture, name }) => (
                <Image key={key} src={picture} title={name} />
            ));
            const value =
                patterns.length > 0 ? renderWithSeparator(patterns, ' ') : 'No patterns selected';
            return withLabel('patterns_to_avoid', value);
        }
    },
    necklines_to_avoid: {
        icon: inbox.icons.necklines_to_avoid,
        formatter: ({ style }) => {
            const necklines = extractValues(style?.necklines_to_avoid);
            return withLabel('necklines_to_avoid', necklines.join(', '));
        }
    },
    clothing_preferences: {
        icon: inbox.icons.clothing_preferences,
        formatter: ({ style }) =>
            withLabel('clothing_preferences', style?.clothing_preferences?.value)
    },
    fit_top: {
        icon: inbox.icons.fit_top,
        formatter: ({ style }) => {
            const fitTop = extractValues(style?.fit_top);
            return withLabel('fit_top', fitTop.join(', '));
        }
    },
    fit_bottom: {
        icon: inbox.icons.fit_bottom,
        formatter: ({ style }) => {
            const fitBottom = extractValues(style?.fit_bottom);
            return withLabel('fit_bottom', fitBottom.join(', '));
        }
    },
    jeans: {
        icon: inbox.icons.jeans,
        formatter: ({ style }) => {
            const jeans = extractValues(style?.jeans);
            return withLabel('jeans', jeans.join(', '));
        }
    },
    fabrics: {
        icon: inbox.icons.fabrics,
        formatter: ({ style }) => {
            const value = extractValues(style?.fabrics).join(', ');
            return withLabel('fabrics', value);
        }
    },
    comfort_zone: {
        icon: inbox.icons.comfort_zone,
        formatter: ({ style }) => withLabel('comfort_zone', style?.comfort_zone?.value)
    },
    heels: {
        icon: inbox.icons.heels,
        formatter: ({ style }) => {
            const value = [
                style?.heels?.value,
                ...extractValues(style?.heels_type),
                style?.heels_type_comments
            ]
                .filter(Boolean)
                .join(', ');
            return withLabel('heels', value);
        }
    },
    jewelry: {
        icon: inbox.icons.jewelry,
        formatter: ({ style }) => {
            const value = extractValues(style?.jewelry).join(', ');
            return withLabel('jewelry', value);
        }
    },
    what_matters: {
        icon: inbox.icons.what_matters,
        formatter: ({ style }) => {
            const value = [...extractValues(style?.what_matters), style?.what_matters_comments]
                .filter(Boolean)
                .join(', ');
            return withLabel('what_matters', value);
        }
    },
    budget_ranges: {
        icon: inbox.icons.budget_ranges,
        formatter: ({ style }) => {
            const budgets = (style?.budget_ranges ?? []).map((range) => (
                <>
                    {range.name}: <b>{range?.ranges[0]}</b>
                </>
            ));
            return noLabel(renderWithSeparator(budgets));
        }
    },
    body_issues: {
        icon: inbox.icons.body_issues,
        formatter: ({ style }) => {
            const issues = (style?.body_issues || []).map((issue) => issue.value).filter(Boolean).join(', ');
            const allIssuesMap = new Map<string, string>([
                ['Body issues', issues],
                ['Something else', style?.body_issues_other],
                ["Client's note", style?.body_issues_details]
            ]);
            const value = Array.from(allIssuesMap.entries()).map(([key, val], index) => {
                if (val && val.length > 0) {
                    return (
                        <div key={index}>
                            {key}: <b>{val}</b>
                        </div>
                    );
                }
                return null;
            });
            return noLabel(renderWithSeparator(value, ' '));
        }
    }
};
